import React from 'react';
import Styles from './TestSeries.module.css';

const TestSeries = () => {
  const features = [
    {
      icon: "📚",
      title: "Comprehensive Test Series",
      description: "Access a wide range of mock tests, sectional tests, and full-length exams designed to match the latest exam patterns and syllabus."
    },
    {
      icon: "🎯",
      title: "Real Exam Simulation",
      description: "Experience exam-like environments with timed tests, question navigation, and performance evaluation to build confidence before the actual examination."
    },
    {
      icon: "📊",
      title: "Detailed Performance Analytics",
      description: "Gain insights into strengths, weaknesses, accuracy, speed, and overall performance through detailed reports and analytics."
    },
    {
      icon: "🏆",
      title: "All-India Ranking",
      description: "Compare your performance with other candidates through ranking and percentile analysis to understand your competitive standing."
    },
    {
      icon: "✏️",
      title: "Topic & Subject-wise Practice",
      description: "Strengthen specific subjects and topics with targeted practice tests and customized learning paths."
    },
    {
      icon: "⚡",
      title: "Instant Results & Solutions",
      description: "Receive immediate results with answer keys, explanations, and performance summaries after completing tests."
    },
    {
      icon: "📈",
      title: "Progress Tracking",
      description: "Monitor learning progress over time through dashboards that help identify improvement areas and measure preparation effectiveness."
    },
    {
      icon: "🎓",
      title: "Multiple Exam Categories",
      description: "Support for various competitive, academic, recruitment, certification, and entrance examinations through a single platform."
    },
    {
      icon: "📱",
      title: "Anytime, Anywhere Access",
      description: "Practice from desktop, tablet, or mobile devices with secure online access and a user-friendly interface."
    },
    {
      icon: "🎨",
      title: "Personalized Learning Experience",
      description: "Receive test recommendations and performance-based insights to focus on areas requiring improvement."
    },
    {
      icon: "🔒",
      title: "Secure & Reliable Platform",
      description: "Built with modern technology to ensure secure access, reliable performance, and uninterrupted learning experiences."
    },
    {
      icon: "💰",
      title: "Affordable Learning Solutions",
      description: "Provide quality exam preparation tools at accessible pricing, making effective learning available to a wider audience."
    }
  ];

  const whyChoose = [
    "Comprehensive test series for competitive and academic examinations",
    "Real exam-like testing experience",
    "Detailed performance analysis and progress tracking",
    "Affordable learning solutions for students from all backgrounds",
    "Practice anytime, anywhere",
    "Continuous improvement through data-driven insights",
  ];

  return (
    <div className={Styles.testSeriesContainer}>
      {/* Hero Section with Student Image Background */}
      <section className={Styles.heroSection} style={{backgroundImage:`url(${'/testoHeroBanner.jpg'})`}}>
        <div className={Styles.heroGradient}></div>
        <div className={Styles.heroContainer}>
          <div className={Styles.heroContent}>
            <h1 className={Styles.heroTitle}>
              Teston
            </h1>
            <span className={Styles.highlight}>Empowering Success Through Smart Learning</span>
            <p className={Styles.heroSubtitle}>
              Helping students and aspirants prepare confidently for competitive exams through quality practice tests, 
              performance insights, and continuous learning.
            </p>
            <div className={Styles.heroInfo}>
              <p>Access test series for various exams through a single platform, with new exams and practice tests added regularly.</p>
            </div>
            <button 
              className={Styles.ctaButton}
              onClick={() => window.location.href = 'http://teston.in/'}
            >
              Get Started Now
              <span className={Styles.arrowIcon}>→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className={Styles.visionSection}>
        <div className={Styles.visionContainer}>
          <div className={Styles.visionImageWrapper}>
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=500&h=400&fit=crop" 
              alt="Our Vision"
              className={Styles.visionImage}
            />
          </div>
          <div className={Styles.visionContent}>
            <h2>Our Vision</h2>
            <p>
              To make quality exam preparation accessible to every student by providing reliable, affordable, 
              and technology-driven learning and assessment solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className={Styles.whyChooseSection}>
        <div className={Styles.container}>
          <h2>Why Choose <span className={Styles.highlight}>'TestSeries'</span>?</h2>
          <div className={Styles.whyChooseGrid}>
            {whyChoose.map((item, index) => (
              <div key={index} className={Styles.whyCard}>
                <div className={Styles.checkIcon}>✓</div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supporting India Section */}
      <section className={Styles.supportSection}>
        <div className={Styles.container}>
          <div className={Styles.supportContent}>
            <h2>Supporting India's Future Talent</h2>
            <p>
              We believe that access to quality assessment and learning tools can help students unlock their potential, 
              develop their skills, and contribute to India's future workforce and economic growth.
            </p>
          </div>
          <div className={Styles.supportImageWrapper}>
            <img 
              src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=500&h=350&fit=crop" 
              alt="Indian students success"
              className={Styles.supportImage}
            />
          </div>
        </div>
      </section>

      {/* Graph/Stats Section */}
      <section className={Styles.graphSection}>
        <div className={Styles.container}>
          <h2>Performance Analytics Dashboard</h2>
          <div className={Styles.graphGrid}>
            <div className={Styles.graphCard}>
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=350&fit=crop" 
                alt="Performance Graph"
                className={Styles.graphImage}
              />
              <p>Real-time Performance Tracking</p>
            </div>
            <div className={Styles.graphCard}>
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=350&fit=crop" 
                alt="Analytics Dashboard"
                className={Styles.graphImage}
              />
              <p>Detailed Analytics & Insights</p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className={Styles.featuresSection}>
        <div className={Styles.container}>
          <h2>Key Features</h2>
          <div className={Styles.featuresGrid}>
            {features.map((feature, index) => (
              <div key={index} className={Styles.featureCard}>
                <div className={Styles.featureIcon}>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={Styles.ctaSection}>
        <div className={Styles.container}>
          <h2>Ready to Ace Your Exams?</h2>
          <p>Join thousands of successful students who achieved their dreams with TestSeries</p>
          <button 
            className={Styles.primaryButton}
            onClick={() => window.location.href = 'http://teston.in/'}
          >
            Start Your Free Trial
          </button>
        </div>
      </section>
    </div>
  );
};

export default TestSeries;
